import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useMemo, useState } from 'react';
import { FixedSizeList as List } from 'react-window';

import { ArrowLeft, ArrowRight } from '@travel/icons/ui';

import useDeviceType from '../../utils/useDeviceType';
import Calendar from './Calendar';

import cx from '../../utils/classnames';
import { generateCalendarMonths } from './helpers';
import { DisplayType, PerDayComponentProps } from './index';

import styles from './datePicker.module.scss';

type Props = {
  /** Custom style for wrapper */
  className: string;
  /** End date of selected range */
  endDate?: string;
  /** Flag to define whether the CalendarWeekHeader will be shown on calendars or not */
  isShowWeekHeader: boolean;
  /** Flag to define whether the next/prev will be shown on the date-picker or not */
  isShowNavigation: boolean;
  /** Number of displayed months (The first month will be started from referenceDate by default) */
  numberOfMonths: number;
  /** Callback function when calendar day is clicked */
  onClickDayItem: (event: React.MouseEvent, day: Dayjs) => void;
  /** Date to define the first calendar */
  referenceDate: string;
  /** Start date of selected range */
  startDate?: string;
  /** Decides using popup or dialog for calendar container */
  type: DisplayType;
  /** week type:
   * 'week': start from Sunday
   * 'isoWeek': start from Monday
   */
  weekType?: 'week' | 'isoWeek';
  /** Array of string to define title of day (normal-ordering) */
  weekdayLabels?: string[];
  /** Array of string to define title of month */
  monthLabels?: string[];
  /** Date to end of reference */
  referenceEndDate?: string;
  /** Flag to highlight the current date */
  shouldShowCurrentDate?: boolean;
  dayWrapperClassName?: string;
  /** Extra component to display on calendar per day */
  PerDayComponent?: React.ComponentType<PerDayComponentProps>;
  /** Custom Height for Calendar of each month */
  customCalendarHeight?: number;
  /** Custom format for display */
  labelDateFormat?: string;
  /** Custom style for wrapper of calendar week headers */
  calendarWeekHeaderClassName?: string;
  /** Custom style for calendar body wrapper (table) */
  calendarBodyClass?: string;
  /** Date Selection Highlight type (default circle) */
  highlightType?: 'circle' | 'full';
  /** Flag to hide any day hovering */
  shouldDisableDayHover?: boolean;
};

const ICON_SIZE = 24;

function DatePickerBody(props: Props) {
  const {
    className,
    numberOfMonths,
    referenceDate,
    endDate,
    startDate,
    onClickDayItem,
    isShowNavigation,
    isShowWeekHeader,
    type,
    weekType,
    weekdayLabels,
    monthLabels,
    referenceEndDate,
    shouldShowCurrentDate,
    dayWrapperClassName,
    PerDayComponent,
    customCalendarHeight = 300,
    labelDateFormat,
    calendarWeekHeaderClassName,
    calendarBodyClass,
    highlightType = 'circle',
    shouldDisableDayHover = false,
  } = props;

  // hover state and handler
  const isPC = useDeviceType() === 'pc';
  const [hoveredDate, setHoveredDate] = useState<string>('');
  const onHoverDayItem = useCallback((_event: React.MouseEvent, day?: Dayjs) => {
    setHoveredDate(prevState => {
      const nextHoveredDate = day?.format('YYYY-MM-DD');
      if (nextHoveredDate && nextHoveredDate !== prevState) {
        return nextHoveredDate;
      }

      return prevState;
    });
  }, []);

  // next/prev state and handler
  // CalendarDate will be used to determine what the first calendar should be.
  const [startCalendarDate, setStartCalendarDate] = useState(
    type === 'popup' ? startDate || referenceDate : referenceDate,
  );

  const isInvalidPrevMonth = dayjs(startCalendarDate).isSame(referenceDate, 'month');
  const isInvalidNextMonth =
    referenceEndDate && dayjs(startCalendarDate).isSame(referenceEndDate, 'month');

  const onClickNext = useCallback(() => {
    setStartCalendarDate(prevState =>
      dayjs(prevState)
        .add(1, 'month')
        .toString(),
    );
  }, []);
  const onClickPrev = useCallback(() => {
    setStartCalendarDate(prevState =>
      dayjs(prevState)
        .subtract(1, 'month')
        .toString(),
    );
  }, []);

  // generate months to be rendered
  const calendarMonths = useMemo(() => generateCalendarMonths(startCalendarDate, numberOfMonths), [
    startCalendarDate,
    numberOfMonths,
  ]);

  // default calendar props
  const defaultCalendarProps = {
    startDate,
    endDate,
    hoveredDate,
    onHoverDayItem: isPC && !shouldDisableDayHover ? onHoverDayItem : undefined,
    onClickDayItem,
    isShowWeekHeader,
    weekType,
    weekdayLabels,
    monthLabels,
    dayWrapperClassName,
    PerDayComponent,
    labelDateFormat,
    calendarWeekHeaderClassName,
    calendarBodyClass,
    highlightType,
  };

  return (
    <div className={cx(className, styles.datePicker)}>
      {type === 'dialog' && (
        // 1000px should be high enough for all of the supported mobiles
        <List
          itemCount={calendarMonths.length}
          height={1000}
          width="100%"
          itemSize={customCalendarHeight}
        >
          {({ index, style }) => (
            <Calendar
              style={style}
              key={calendarMonths[index]}
              referenceDate={referenceDate}
              referenceEndDate={referenceEndDate}
              shouldShowCurrentDate={shouldShowCurrentDate}
              calendarDate={calendarMonths[index]}
              {...defaultCalendarProps}
            />
          )}
        </List>
      )}
      {type === 'popup' && (
        <>
          {calendarMonths.map(month => (
            <Calendar
              key={month}
              referenceDate={referenceDate}
              referenceEndDate={referenceEndDate}
              shouldShowCurrentDate={shouldShowCurrentDate}
              calendarDate={month}
              {...defaultCalendarProps}
            />
          ))}
        </>
      )}
      {isShowNavigation && (
        <>
          <ArrowLeft
            className={cx(styles.icon, styles.left, isInvalidPrevMonth && styles.invalid)}
            role="button"
            onClick={onClickPrev}
            size={ICON_SIZE}
          />
          <ArrowRight
            className={cx(styles.icon, styles.right, isInvalidNextMonth && styles.invalid)}
            role="button"
            onClick={onClickNext}
            size={ICON_SIZE}
          />
        </>
      )}
    </div>
  );
}

DatePickerBody.defaultProps = {
  className: '',
  isActive: false,
  isShowWeekHeader: true,
  isShowNavigation: false,
  numberOfMonths: 1,
};

export default DatePickerBody;
